import React, { useEffect, useState } from "react";
import { CashTransfer } from "../../types/cashTransfer.type";
import { api } from "../../api/api";
import {
  Button,
  Checkbox,
  DatePicker,
  Dropdown,
  Form,
  Input,
  InputNumber,
  Menu,
  message,
  Modal,
  Popconfirm,
  Select,
  Space,
  Table,
  Typography,
} from "antd";
import { Currency } from "../../types/currency.type";
import useGetRequest from "../../hooks/useGetRequest";
import { Cashbox } from "../../types/cashbox.type";
import { PiMicrosoftExcelLogoFill } from "react-icons/pi";
import { useDownloadFile } from "../../hooks/useDownloadFile";
import { useAuth } from "../../contexts/auth.context";
import { UserRole } from "../../types/user.type";
import { canEditCashTransfers } from "../../utils/roleUtils";
import { ICashTransferClient } from "../../types/cashTransferClient.type";
import { DownOutlined } from "@ant-design/icons";
import { roundToThreeDecimals } from "../../utils/roundToThreeDecimals";
import { useNavigate, useParams } from "react-router-dom";
import dayjs from "dayjs";
import { useExchangeRate } from "../../contexts/exchangeRate.context";
import { Machine } from "../../types/machine.type";

const { Title } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

export default function CashTransfers() {
  const navigate = useNavigate();
  const { id, name } = useParams();
  const { authState } = useAuth();
  const { data: cashboxesList, loading: cashboxesListLoading } =
    useGetRequest("/cashbox/list");
  const { data: clientsList, loading: clientsListLoading } = useGetRequest(
    "/cash-transfer/list/clients"
  );
  const [transfers, setTransfers] = useState<CashTransfer[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [total, setTotal] = useState<number>(0);
  const [cashboxId, setCashboxId] = useState<string | undefined>(undefined);
  const [sortBy, setSortBy] = useState<string>("date");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [currency, setCurrency] = useState<string | undefined>(undefined);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState<boolean>(false);
  const [hasDebt, setHasDebt] = useState<boolean>(false);
  const [overdue, setOverdue] = useState<boolean>(false);
  const [convertedAmount, setConvertedAmount] = useState<number | null>(null);
  const [amount, setAmount] = useState<number | null>(null);
  const [commissionPercentage, setCommissionPercentage] = useState<
    number | null
  >(null);
  const [toCashboxCurrency, setToCashboxCurrency] = useState<
    Currency | undefined
  >();
  const [fromCashboxCurrency, setFromCashboxCurrency] = useState<
    Currency | undefined
  >();
  const { exchangeRate } = useExchangeRate();
  const [form] = Form.useForm();
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [paymentForm] = Form.useForm();
  const [selectedCashTransfer, setSelectedCashTransfer] =
    useState<CashTransfer | null>(null);
  const { loading: downloadLoading, downloadFile } = useDownloadFile();
  const [dateRange, setDateRange] = useState<
    [dayjs.Dayjs, dayjs.Dayjs] | undefined
  >([dayjs().startOf("month"), dayjs().endOf("month")]);
  const { data: machineList, loading: machineListLoading } =
    useGetRequest("/machine/list");

  const handleRangeChange = (dates: any) => {
    if (dates) {
      setDateRange([dates[0], dates[1]]);
    } else {
      setDateRange(undefined);
    }
  };

  const handleCashboxChange = (value: string, isFromCashbox: boolean) => {
    const cashbox = cashboxesList?.find((cb: Cashbox) => cb._id === value);
    if (isFromCashbox) {
      setFromCashboxCurrency(cashbox.currency);
    } else {
      setToCashboxCurrency(cashbox.currency);
    }
    setConvertedAmount(null);
  };

  const calculateConvertedAmount = (
    amount: number,
    exchangeRate: number,
    commissionPercentage: number,
    fromCashboxCurrency: Currency,
    toCashboxCurrency: Currency
  ) => {
    const amountAfterCommission =
      amount - amount * (commissionPercentage / 100);

    let convertedAmount = null;
    if (
      fromCashboxCurrency === Currency.US_DOLLAR &&
      toCashboxCurrency === Currency.UZBEK_SOM
    ) {
      convertedAmount = amountAfterCommission * exchangeRate;
    } else if (
      fromCashboxCurrency === Currency.UZBEK_SOM &&
      toCashboxCurrency === Currency.US_DOLLAR
    ) {
      convertedAmount = amountAfterCommission / exchangeRate;
    } else if (fromCashboxCurrency === toCashboxCurrency) {
      convertedAmount = amountAfterCommission;
    }

    return convertedAmount;
  };

  useEffect(() => {
    if (
      amount !== null &&
      exchangeRate !== null &&
      fromCashboxCurrency &&
      toCashboxCurrency
    ) {
      const result = calculateConvertedAmount(
        amount,
        exchangeRate,
        commissionPercentage!,
        fromCashboxCurrency!,
        toCashboxCurrency
      );
      setConvertedAmount(result);
    }
  }, [
    amount,
    exchangeRate,
    fromCashboxCurrency,
    toCashboxCurrency,
    commissionPercentage,
  ]);

  useEffect(() => {
    if (exchangeRate) {
      form.setFieldsValue({ exchangeRate });
    }
  }, [exchangeRate, form]);

  const fetchTransfers = async () => {
    setLoading(true);
    try {
      const response = await api.get("/cash-transfer", {
        params: {
          page,
          limit: pageSize,
          startDate: dateRange ? dateRange[0]?.format("DD.MM.YY") : undefined,
          endDate: dateRange ? dateRange[1]?.format("DD.MM.YY") : undefined,
          cashboxId,
          sortBy,
          order: sortOrder,
          currency,
          clientId: id,
          hasDebt: hasDebt ? true : undefined,
          overdue: overdue ? true : undefined,
        },
      });

      setTransfers(response.data.transfers);
      setTotal(response.data.total);
    } catch (error) {
      console.error(error);
      message.error("Ошибка получение конвертаций");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTransfers();
  }, [
    page,
    pageSize,
    dateRange,
    cashboxId,
    sortBy,
    sortOrder,
    currency,
    id,
    name,
    hasDebt,
    overdue,
  ]);

  const handleExport = () => {
    const exportParams = {
      startDate: dateRange ? dateRange[0]?.format("DD.MM.YY") : undefined,
      endDate: dateRange ? dateRange[1]?.format("DD.MM.YY") : undefined,
    };
    downloadFile(
      "/export/cash-transfers",
      "cash_transfers_data.xlsx",
      exportParams
    );
  };

  const handleCreatePayment = async (values: any) => {
    setConfirmLoading(true);

    try {
      const data = {
        ...values,
        exchangeRate: exchangeRate || values.exchangeRate,
      };

      await api.post(
        `/cash-transfer/${selectedCashTransfer?._id}/payment`,
        data
      );
      message.success("Выплата успешно добавлена.");

      fetchTransfers();
      setIsPaymentModalOpen(false);
      setConfirmLoading(false);
      paymentForm.resetFields();
    } catch (error: any) {
      setConfirmLoading(false);
      console.error("Error creating transaction", error);
      message.error(error.response.data.error);
    }
  };

  const handleDeleteTrasfer = async (id: string) => {
    try {
      await api.delete(`/cash-transfer/${id}`);

      message.success("Конвертация успешно удалена.");
      fetchTransfers();
    } catch (error: any) {
      message.error(error.response.data.error);
    }
  };

  const columns = [
    {
      title: "Дата",
      dataIndex: "date",
      key: "date",
    },
    {
      title: "Дата обещанного перевода",
      dataIndex: "promisedDate",
      key: "promisedDate",
      render: (text: string | null | undefined) => {
        return text ? dayjs(text).format("DD-MM-YYYY") : "Н/Д";
      },
    },
    {
      title: "Клиент",
      dataIndex: "clientName",
      key: "clientName",
    },
    {
      title: "Валюта",
      dataIndex: "currency",
      key: "currency",
    },
    {
      title: "Сумма",
      dataIndex: "amount",
      key: "amount",
      render: (amount: number) => roundToThreeDecimals(amount),
    },
    {
      title: "Сумма к зачислению",
      dataIndex: "total",
      key: "total",
      render: (amount: number) => roundToThreeDecimals(amount),
    },
    {
      title: "Выплачено",
      dataIndex: "paid",
      key: "paid",
    },
    {
      title: "Остаток",
      key: "remaining",
      render: (_: any, cashTransfer: CashTransfer) =>
        roundToThreeDecimals(cashTransfer.total - cashTransfer.paid),
    },
    {
      title: "Описание",
      dataIndex: "description",
      key: "description",
    },
    {
      title: "C кассы",
      dataIndex: "fromCashboxName",
      key: "fromCashboxName",
    },
    {
      title: "Коммисия",
      dataIndex: "commission",
      key: "commission",
      render: (commission: number, record: CashTransfer) => {
        return `${roundToThreeDecimals(commission)} (${record.commissionPercentage
          }%)`;
      },
    },
    {
      title: "Пользователь",
      dataIndex: "ownerUsername",
      key: "ownerUsername",
    },
    {
      title: "Машина",
      dataIndex: "machineName",
      key: "machineName",
    },
    {
      title: "",
      key: "actions",
      render: (_: any, cashTransfer: CashTransfer) => {
        const menu = (
          <Menu>
            <Menu.Item
              onClick={() => {
                setIsPaymentModalOpen(true);
                setSelectedCashTransfer(cashTransfer);
              }}
            >
              Выплата
            </Menu.Item>
            <Menu.Item
              onClick={() =>
                navigate(
                  `/payments/${cashTransfer.clientName}/${cashTransfer._id}`
                )
              }
            >
              Смотреть выплаты
            </Menu.Item>
            <Menu.Item>
              <Popconfirm
                title="Вы уверены, что хотите удалить эту конвертацию?"
                onConfirm={() => handleDeleteTrasfer(cashTransfer._id)}
                okText="Да"
                cancelText="Нет"
              >
                Удалить
              </Popconfirm>
            </Menu.Item>
          </Menu>
        );

        return (
          <Dropdown overlay={menu}>
            <Button type="link">
              Действия <DownOutlined />
            </Button>
          </Dropdown>
        );
      },
    },
  ];

  const handleSubmit = async (values: Partial<CashTransfer>) => {
    setConfirmLoading(true);
    try {
      const data = {
        ...values,
        exchangeRate: exchangeRate || values.exchangeRate,
      };

      await api.post("/cash-transfer", data);
      message.success("Конвертация успешно добавлена.");
    } catch (error: any) {
      console.error("Error saving transfer:", error);
      message.error(error.response.data.error);
    } finally {
      setConfirmLoading(false);
      setIsModalOpen(false);
      fetchTransfers();
      form.resetFields();
    }
  };

  return (
    <div style={{ padding: 24 }}>
      <Title level={2}>Конвертации {name}</Title>
      <Space style={{ marginBottom: 16 }}>
        <RangePicker
          onChange={handleRangeChange}
          placeholder={["Начало", "Конец"]}
          format="DD.MM.YY"
          value={dateRange}
        />
        <Select
          placeholder="Сортировать по"
          defaultValue="date"
          onChange={(value) => setSortBy(value)}
          style={{ width: 200 }}
        >
          <Option value="date">Дата</Option>
          <Option value="amount">Сумма</Option>
          <Option value="commission">Коммисия</Option>
        </Select>
        <Select
          defaultValue="desc"
          onChange={(value: "asc" | "desc") => setSortOrder(value)}
          style={{ width: 150 }}
        >
          <Option value="asc">По возрастанию</Option>
          <Option value="desc">По убыванию</Option>
        </Select>
        <Select
          allowClear
          placeholder="Фильтр по валюте"
          onChange={(value) => setCurrency(value)}
          style={{ width: 150 }}
        >
          {Object.values(Currency).map((Currency) => (
            <Option key={Currency} value={Currency}>
              {Currency}
            </Option>
          ))}
        </Select>
        <Select
          placeholder="Фильтр по кассе"
          allowClear
          loading={cashboxesListLoading}
          onChange={(value) => setCashboxId(value)}
        >
          {cashboxesList?.map((cashbox: Cashbox) => (
            <Option key={cashbox._id} value={cashbox._id}>
              Имя: {cashbox.name} | Валюта: {cashbox.currency}
            </Option>
          ))}
        </Select>
        <Checkbox
          checked={hasDebt}
          onChange={(e) => setHasDebt(e.target.checked)}
        >
          Есть долг
        </Checkbox>
        <Checkbox
          checked={overdue}
          onChange={(e) => setOverdue(e.target.checked)}
        >
          Просрочено
        </Checkbox>
        {canEditCashTransfers(authState.user?.role) && (
            <Button type="primary" onClick={() => setIsModalOpen(true)}>
              Добавить
            </Button>
          )}
        <Button
          type="default"
          loading={downloadLoading}
          icon={<PiMicrosoftExcelLogoFill />}
          onClick={handleExport}
        >
          Экспорт в Excel
        </Button>
      </Space>
      <Table
        loading={loading}
        columns={columns}
        dataSource={transfers}
        rowKey={(record) => record._id}
        pagination={{
          current: page,
          total: total,
          pageSize: pageSize,
          onChange: (page, size) => {
            setPage(page);
            if (size !== pageSize) setPageSize(size!);
          },
          showSizeChanger: true,
        }}
        scroll={{ x: "max-content" }}
      />
      <Modal
        title="Добавить конвертацию"
        open={isModalOpen}
        confirmLoading={confirmLoading}
        onCancel={() => setIsModalOpen(false)}
        onOk={() => form.submit()}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            name="clientId"
            label="Клиента"
            rules={[{ required: true, message: "Выберите клиента" }]}
          >
            <Select 
              placeholder="Выберите клиента" 
              loading={clientsListLoading}
              showSearch
              filterOption={(input, option) => {
                const optionText = option?.label || option?.children;
                return String(optionText).toLowerCase().includes(input.toLowerCase());
              }}
              optionFilterProp="children"
            >
              {clientsList?.map((client: ICashTransferClient) => (
                <Option key={client._id} value={client._id}>
                  {client.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="amount"
            label="Сумма"
            rules={[{ required: true, message: "Пожалуйста, введите сумму" }]}
          >
            <InputNumber
              value={amount}
              onChange={(value) => setAmount(value)}
              style={{ width: "100%" }}
            />
          </Form.Item>

          <Form.Item
            name="description"
            label="Описание"
            rules={[
              { required: true, message: "Пожалуйста, введите описание" },
            ]}
          >
            <Input.TextArea />
          </Form.Item>
          <Form.Item
            name="fromCashboxId"
            label="C кассы"
            rules={[{ required: true, message: "Выберите кассу" }]}
          >
            <Select
              placeholder="Выберите кассу"
              loading={loading}
              onChange={(value) => handleCashboxChange(value, true)}
            >
              {cashboxesList?.map((cashbox: Cashbox) => (
                <Option key={cashbox._id} value={cashbox._id}>
                  Имя: {cashbox.name} | Валюта: {cashbox.currency}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="exchangeRate"
            label="Курс"
            hidden={true}
          >
            <InputNumber value={exchangeRate} style={{ width: "100%" }} />
          </Form.Item>

          <Form.Item
            name="commissionPercentage"
            label="Коммисия (%)"
            rules={[
              { required: true, message: "Пожалуйста, введите коммисия" },
            ]}
          >
            <InputNumber
              value={commissionPercentage}
              onChange={(value) => setCommissionPercentage(value)}
              style={{ width: "100%" }}
            />
          </Form.Item>
          <Form.Item
            name="machineId"
            label="Машина"
            rules={[{ required: true, message: "Выберите машину" }]}
          >
            <Select 
              placeholder="Выберите машину" 
              loading={machineListLoading}
              showSearch
              filterOption={(input, option) => {
                const optionText = option?.label || option?.children;
                return String(optionText).toLowerCase().includes(input.toLowerCase());
              }}
              optionFilterProp="children"
            >
              {machineList?.map((machine: Machine) => (
                <Option key={machine._id} value={machine._id}>
                  {machine.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="promisedDate"
            label="Дата обещанного перевода"
            rules={[{ required: true, message: "Пожалуйста, выберите дату" }]}
          >
            <DatePicker style={{ width: "100%" }} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Добавить выплату"
        open={isPaymentModalOpen}
        onCancel={() => setIsPaymentModalOpen(false)}
        onOk={() => paymentForm.submit()}
        confirmLoading={confirmLoading}
      >
        <Form
          form={paymentForm}
          onFinish={handleCreatePayment}
          layout="vertical"
        >
          <Form.Item
            name="toCashboxId"
            label="На кассу"
            rules={[{ required: true, message: "Выберите кассу" }]}
          >
            <Select
              placeholder="Выберите кассу"
              loading={loading}
              onChange={(value) => handleCashboxChange(value, false)}
            >
              {cashboxesList?.map((cashbox: Cashbox) => (
                <Option key={cashbox._id} value={cashbox._id}>
                  Имя: {cashbox.name} | Валюта: {cashbox.currency}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="amount"
            label="Сумму"
            rules={[{ required: true, message: "Введите сумму" }]}
          >
            <InputNumber style={{ width: "100%" }} />
          </Form.Item>

          <Form.Item
            name="exchangeRate"
            label="Курс"
            hidden={true}
          >
            <InputNumber value={exchangeRate} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item
            name="description"
            label="Описание"
            rules={[
              { required: true, message: "Пожалуйста, введите описание" },
            ]}
          >
            <Input.TextArea />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
